import { ArrowLeftOutlined } from "@ant-design/icons"
import { Button, Flex, Typography } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"
import { type BlockerFunction, Link } from "react-router-dom"

import { FixedContainer } from "../../../components/FixedContainer.tsx"
import ReactRouterPrompt from "../../../components/ReactRouterPrompt.tsx"
import { AddressQuickLinks } from "../../../components/Shared/AddressQuickLinks.tsx"
import { RoutesPaths } from "../../../constants/routes.ts"
import { getEntityName } from "../../../utils/user/get-entity-name"
import { WorkspaceUnsavedChangesModal } from "../../Lead/LeadWorkspacePage/cards/WorkspaceUnsavedChangesModal.tsx"
import {
  WorkspaceEditGuardProvider,
  useWorkspaceEditGuard
} from "../../Lead/LeadWorkspacePage/cards/workspace-edit-guard.tsx"
import { type LeadListItem } from "../../Leads/types/types.ts"
import { type ProjectListItem } from "../../Projects/types/types.ts"
import {
  type Customer,
  type CustomerMutateData
} from "../types/customer.types.ts"
import { CustomerAccountCard } from "./cards/CustomerAccountCard.tsx"
import { CustomerFieldsCard } from "./cards/CustomerFieldsCard.tsx"
import { CUSTOMER_CARDS } from "./cards/customer-card-fields.ts"
import { type CustomerRelatedList } from "./redux/customer-detail.types.ts"
import { CustomerRelatedLeads } from "./related/CustomerRelatedLeads.tsx"
import { CustomerRelatedProjects } from "./related/CustomerRelatedProjects.tsx"

const { Title } = Typography

interface CustomerDetailProps {
  customer: Customer
  canEdit?: boolean
  leads: CustomerRelatedList<LeadListItem>
  projects: CustomerRelatedList<ProjectListItem>
  onSave: (changed: CustomerMutateData) => Promise<boolean>
  onLeadsLoadMore: () => void
  onProjectsLoadMore: () => void
}

function CustomerDetailBody({
  customer,
  canEdit,
  leads,
  projects,
  onSave,
  onLeadsLoadMore,
  onProjectsLoadMore
}: CustomerDetailProps) {
  const { t } = useTranslation()
  const { hasUnsavedChanges, discardAll } = useWorkspaceEditGuard()

  const blockNavigation: BlockerFunction = () => hasUnsavedChanges

  return (
    <FixedContainer>
      <Flex
        align={"center"}
        gap={"small"}
        data-testid={"customer-detail-header"}
      >
        <Link to={RoutesPaths.customers.list}>
          <Button type={"text"} icon={<ArrowLeftOutlined />}>
            {t("customerDetail.back")}
          </Button>
        </Link>
        <Title level={3} className={"m-0"}>
          {getEntityName(customer, t("customerDetail.unnamed"))}
        </Title>
      </Flex>

      <div
        className={"grid grid-cols-1 lg:grid-cols-2 gap-4 items-start"}
        data-testid={"customer-detail-cards"}
      >
        <Flex vertical gap={"middle"} className={"min-w-0"}>
          <CustomerFieldsCard
            spec={CUSTOMER_CARDS.profile}
            customer={customer}
            canEdit={canEdit}
            onSave={onSave}
          />
          <CustomerFieldsCard
            spec={CUSTOMER_CARDS.contact}
            customer={customer}
            canEdit={canEdit}
            onSave={onSave}
          />
          <CustomerFieldsCard
            spec={CUSTOMER_CARDS.address}
            customer={customer}
            canEdit={canEdit}
            onSave={onSave}
            viewExtra={
              <AddressQuickLinks
                className="mt-3"
                address={customer.address}
                address_2={customer.address_2}
                city={customer.city}
                state={customer.state}
                zip_code={customer.zip_code}
              />
            }
          />
        </Flex>

        <Flex vertical gap={"middle"} className={"min-w-0"}>
          <CustomerAccountCard customer={customer} />
          <CustomerFieldsCard
            spec={CUSTOMER_CARDS.notes}
            customer={customer}
            canEdit={canEdit}
            onSave={onSave}
          />
        </Flex>
      </div>

      <CustomerRelatedLeads leads={leads} onLoadMore={onLeadsLoadMore} />
      <CustomerRelatedProjects
        projects={projects}
        onLoadMore={onProjectsLoadMore}
      />

      <ReactRouterPrompt when={blockNavigation}>
        {({ isActive, onConfirm, onCancel }) => (
          <WorkspaceUnsavedChangesModal
            open={isActive}
            onStay={onCancel}
            onDiscard={() => {
              discardAll()
              onConfirm()
            }}
          />
        )}
      </ReactRouterPrompt>
    </FixedContainer>
  )
}

function CustomerDetail(props: CustomerDetailProps) {
  return (
    <WorkspaceEditGuardProvider>
      <CustomerDetailBody {...props} />
    </WorkspaceEditGuardProvider>
  )
}

export { CustomerDetail }
